<template>
  <header id="CodementumNavbarHeader" :style="stagingStyle">
    <a
      :href="logoLink"
      class="logo"
      :target="logoLinkTarget"
      style="display: flex"
    >
      <img
        :src="logoUrl"
        height="25"
        style="display: inline-flex; align-self: center"
      />
    </a>

    <div
      v-if="schoolImageURL || distributorImage || schoolName || distributorName"
      class="extra-image-name"
    >
      <img
        class="school-image"
        v-if="schoolImageURL || distributorImage"
        :src="schoolImageURL || distributorImage"
      />
      <div
        class="school-name"
        v-if="schoolName || distributorName"
        v-text="schoolName || distributorName"
      />
    </div>

    <div class="user-settings">
      <slot name="extra"></slot>
    </div>

    <ul>
      <li class="navbar-dropdown">
        <a href="javascript:void(0)" class="dropbtn">
          <span v-show="$i18n.locale === 'tr'">Türkçe</span>
          <span v-show="$i18n.locale === 'en'">English</span>
          <span v-show="$i18n.locale === 'de'">Deutsch</span>
          <span v-show="$i18n.locale === 'es'">Español</span>
          <span v-show="$i18n.locale === 'nl'">Nederlands</span>
        </a>
        <div class="dropdown-content">
          <a
            href="#"
            v-if="$i18n.availableLocales.includes('en')"
            @click.prevent="setLang('en')"
            ><span>English</span></a
          >
          <a
            href="#"
            v-if="$i18n.availableLocales.includes('de')"
            @click.prevent="setLang('de')"
            ><span>Deutsch</span></a
          >
          <a
            href="#"
            v-if="$i18n.availableLocales.includes('es')"
            @click.prevent="setLang('es')"
            ><span>Español</span></a
          >
          <a
            href="#"
            v-if="$i18n.availableLocales.includes('nl')"
            @click.prevent="setLang('nl')"
            ><span>Nederlands</span></a
          >
          <a v-if="isFromTR" href="#" @click.prevent="setLang('tr')"
            ><span>Türkçe</span></a
          >
        </div>
      </li>

      <template v-if="isAuthenticated">
        <li class="navbar-dropdown">
          <a href="javascript:void(0)" class="dropbtn">{{ displayName }}</a>
          <div class="dropdown-content auth-menu">
            <a :href="panelUrl + 'dashboard'" :target="menuLinkTarget">
              <Icon icon="fa-solid:th" /> {{ $t('menu.dashboard') }}
            </a>
            <a
              :href="panelUrl + 'account/profile/info'"
              :target="menuLinkTarget"
            >
              <Icon icon="fa-solid:user" /> {{ $t('menu.profile') }}
            </a>
            <a
              v-if="isTeacher || isDeveloper"
              :href="panelUrl + 'teacher/classes'"
              :target="menuLinkTarget"
            >
              <Icon icon="fa-solid:chalkboard-teacher" />
              {{ $t('menu.teachers') }}
            </a>
            <a
              v-if="isManager || isDeveloper"
              :href="panelUrl + 'management/'"
              :target="menuLinkTarget"
            >
              <Icon icon="fa-solid:user-tie" /> {{ $t('menu.management') }}
            </a>
            <a
              v-if="isDeveloper || isManager"
              href="javascript:void(0)"
              @click="$bvModal.show('deployment-modal')"
            >
              <Icon icon="fa-solid:rocket" /> Deployment
            </a>
            <div class="dropdown-divider" />
            <a href="javascript:void(0)" @click="logout">
              <Icon icon="fa-solid:sign-out-alt" /> {{ $t('menu.logout') }}
            </a>
          </div>
        </li>
        <li>
          <template v-if="isPanel">
            <router-link
              v-if="purchaseButton.show"
              class="purchase-button"
              to="/purchase"
            >
              {{ $t('purchase') }} <img :src="purchaseUrl" height="100%" />
            </router-link>
          </template>
          <template v-else>
            <a
              v-if="purchaseButton.show"
              class="purchase-button"
              :href="panelUrl + 'purchase'"
            >
              {{ $t('purchase') }} <img :src="purchaseUrl" height="100%" />
            </a>
          </template>
        </li>
      </template>
      <template v-else>
        <li>
          <a
            class="login-button"
            href="#"
            @click="redirectToLogin"
            v-text="$t('login')"
          />
        </li>
        <li>
          <a
            class="register-button"
            href="#"
            @click="redirectToRegister"
            v-text="$t('register')"
          />
        </li>
      </template>
    </ul>
    <DeploymentModal />
  </header>
</template>

<script>
import { CodementumUrl } from '@cm-frontend/shared/codementum-url';
import { CookieManager } from '@cm-frontend/shared/cookie-manager';
import { useAccount, useDistributorCompany } from '@cm-frontend/shared/queries';

import logoUrl from './assets/logo.png';
import purchaseUrl from './assets/purchase.svg';
import DeploymentModal from './components/DeploymentModal.vue';
import cookieJwt from './store/cookieJwt.js';

export default {
  name: 'CodementumNavbar',

  components: {
    DeploymentModal
  },

  props: {
    logoLink: { type: String, default: '#' },
    logoLinkTarget: { type: String, default: '_self' },
    menuLinkTarget: { type: String, default: '_blank' }
  },

  setup() {
    const account = useAccount();
    const distributor = useDistributorCompany();
    return { account, distributor };
  },

  data() {
    return {
      logoUrl,
      purchaseUrl,
      purchaseButton: { show: true }
    };
  },

  computed: {
    // From vue-query account
    isAuthenticated() {
      return this.account?.isAuthenticated ?? false;
    },
    isFromTR() {
      return this.account?.isFromTR ?? false;
    },
    displayName() {
      return this.account?.fullName ?? '';
    },
    isTeacher() {
      return this.account?.isTeacher ?? false;
    },
    isManager() {
      return this.account?.isManager ?? false;
    },
    isDeveloper() {
      return this.account?.isDeveloper ?? false;
    },
    schoolImageURL() {
      return this.account?.schoolImageURL ?? null;
    },
    schoolName() {
      return this.account?.schoolName ?? null;
    },

    // From vue-query distributor
    distributorImage() {
      return this.distributor?.distributorImage ?? null;
    },
    distributorName() {
      return this.distributor?.distributorName ?? null;
    },
    distributorOptionsCSS() {
      return this.distributor?.distributorOptionsCSS ?? {};
    },

    // From CodementumUrl static (no Vuex)
    panelUrl() {
      return CodementumUrl.panel();
    },
    stagingStyle() {
      return CodementumUrl.buildMode === 'staging'
        ? 'border-top: 5px solid pink'
        : '';
    },

    isPanel() {
      return (
        window.location.hostname.includes('panel.codementum') ||
        window.location.hostname.includes('localhost:8081')
      );
    }
  },

  watch: {
    distributorOptionsCSS: {
      immediate: true,
      handler(css) {
        const root = document.querySelector(':root');
        Object.keys(css).forEach((key) =>
          root.style.setProperty(key, css[key])
        );
      }
    },
    account: {
      immediate: true,
      handler(accountData) {
        const language = accountData?.settings?.language;
        if (language) {
          this.$i18n.locale = language;
          if (this.$root.$i18n) this.$root.$i18n.locale = language;
        }
        if (accountData?.isPaid) this.purchaseButton.show = false;
      }
    }
  },

  methods: {
    redirectToLogin() {
      window.location =
        CodementumUrl.panel('login') + '?return=' + window.location;
    },
    redirectToRegister() {
      window.location = CodementumUrl.panel('register');
    },
    reload() {
      cookieJwt(this.$store);
    },
    async logout() {
      const redirectURI = CookieManager.get('redirectURI') || null;
      await this.$store.dispatch('CodementumUser/logout');
      const isPanel = ['panel.codementum', 'localhost:8080'].some((ch) =>
        window.location.href.includes(ch)
      );
      if (!isPanel && redirectURI) return (window.location = redirectURI);
      this.$emit('logout', redirectURI);
    },
    setLang(lang) {
      this.$i18n.locale = lang;
      if (this.$root.$i18n) this.$root.$i18n.locale = lang;
      this.$store.dispatch('CodementumUser/updateLanguage', lang);
    }
  },

  i18n: {
    messages: {
      en: {
        chooseLang: 'Language',
        login: 'Login',
        register: 'Register',
        purchase: 'Purchase',
        menu: {
          dashboard: 'Dashboard',
          profile: 'My Profile',
          teachers: 'Teachers',
          management: 'Management',
          logout: 'Log out'
        }
      },
      tr: {
        chooseLang: 'Dil',
        login: 'Giriş Yap',
        register: 'Kayıt Ol',
        purchase: 'Satin Al',
        menu: {
          dashboard: 'Benim Sayfam',
          profile: 'Profilim',
          teachers: 'Öğretmenler',
          management: 'Yönetim',
          logout: 'Çıkış Yap'
        }
      },
      de: {
        chooseLang: 'Sprache',
        login: 'Einloggen',
        register: 'Anmelden',
        purchase: 'Kauf',
        menu: {
          dashboard: 'Dashboard',
          profile: 'Mein Profil',
          teachers: 'Lehrer',
          management: 'Management',
          logout: 'Ausloggen'
        }
      },
      nl: {
        chooseLang: 'Taal',
        login: 'Log in',
        register: 'Registreren',
        purchase: 'Aankoop',
        menu: {
          dashboard: 'Dashboard',
          profile: 'Mijn profiel',
          teachers: 'Leraren',
          management: 'Beheer',
          logout: 'Uitloggen'
        }
      },
      es: {
        chooseLang: 'Idioma',
        login: 'Login',
        register: 'Registrarse',
        purchase: 'Compra',
        menu: {
          dashboard: 'Tablero',
          profile: 'Mi perfil',
          teachers: 'Profesoras',
          management: 'Gestión',
          logout: 'Cerrar sesión'
        }
      }
    }
  }
};
</script>

<style>
:root {
  --background-primary: #ffae38;
  --background-primary-hover: #ff9838;
  --dropdown-content-text-color: #000000;
  --dropdown-content-text-hover: #000000;
  --dropdown-content-background-hover: #f1f1f1;
  --header-image-height: 40px;
}

svg.iconify {
  width: 1em;
  height: 1em;
}
</style>

<style scoped lang="scss">
header#CodementumNavbarHeader {
  z-index: 999;
  background: var(--background-primary);
  padding-left: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 45px;

  .extra-image-name {
    display: flex;
    margin-left: 0.5rem;
    margin-right: auto;
  }
  .school-name {
    display: inline-flex;
    align-self: center;
    max-width: 120px;
    color: white;
    margin-left: 0.5em;
    line-height: 0.85;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .school-image {
    display: inline-flex;
    align-self: center;
    margin-left: 0.5em;
    height: var(--header-image-height);
  }
  .user-settings {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0 auto;
  }

  ul {
    list-style-type: none;
    margin: 0;
    padding: 0;
    li {
      border-radius: 5px;
      position: relative;
      margin-right: 2em;
      float: left;
      font-family:
        -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue',
        Arial, sans-serif;
      &:focus-within,
      &:hover {
        background-color: var(--background-primary-hover);
      }
      a,
      .dropbtn {
        display: inline-block;
        color: white;
        text-align: center;
        text-decoration: none;
        height: 30px;
        padding: 5px 10px;
      }
      .login-button {
        padding: 5px 10px;
        font-size: 14px;
        border-radius: 5px;
        background-color: #17a2b8;
      }
      .register-button {
        padding: 5px 10px;
        font-size: 14px;
        border-radius: 5px;
        background-color: #28a745;
      }
      .purchase-button {
        img {
          height: 14px;
          margin-top: 1px;
        }
        padding: 5px 10px;
        font-size: 14px;
        border-radius: 5px;
        background-image: linear-gradient(to bottom, #00778f 0%, #17a2b8 100%);
      }
    }
  }

  .dropdown-content {
    min-width: 130px;
    font-size: 14px;
    color: #212529;
    text-align: left;
    background-color: #fff;
    background-clip: padding-box;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 5px;
    display: none;
    position: absolute;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.09);
    z-index: 1;
    top: 30px;
    right: 0;
    &.auth-menu {
      min-width: 160px;
      a {
        display: flex;
        align-items: center;
        & > *:first-child {
          margin-right: 7px;
        }
      }
    }
    a {
      height: unset;
    }
    .dropdown-divider {
      height: 0;
      margin: 5px 0;
      overflow: hidden;
      border-top: 1px solid #e9ecef;
    }
    span {
      padding-left: 5px;
    }
  }
  .dropdown-content a {
    color: var(--dropdown-content-text-color);
    padding: 12px 16px;
    text-decoration: none;
    display: block;
    text-align: left;
  }
  .dropdown-content a:hover {
    color: var(--dropdown-content-text-hover);
    background-color: var(--dropdown-content-background-hover);
  }
  .navbar-dropdown:hover .dropdown-content {
    display: block;
  }
}
</style>
